import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/utils/api';

const emptyForm = { title: '', content: '', is_active: true, countries: [] };

export default function AgreementTemplatesManager() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create mode, object = edit mode
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [allCountries, setAllCountries] = useState([]);
  const [countryQuery, setCountryQuery] = useState('');

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/student-auth/agreement-templates');
      setTemplates(res.data.templates || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const loadCountries = async () => {
    try {
      const res = await apiClient.get('/constants');
      setAllCountries(res.data.countries || []);
    } catch {
      setAllCountries([]);
    }
  };

  useEffect(() => {
    loadTemplates();
    loadCountries();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setCountryQuery('');
    setEditorOpen(true);
  };

  const openEdit = (tpl) => {
    setEditing(tpl);
    setForm({
      title: tpl.title || '',
      content: tpl.content || '',
      is_active: tpl.is_active !== false,
      countries: Array.isArray(tpl.countries) ? tpl.countries : [],
    });
    setCountryQuery('');
    setEditorOpen(true);
  };

  const toggleCountry = (country) => {
    setForm(prev => {
      const list = prev.countries || [];
      return {
        ...prev,
        countries: list.includes(country) ? list.filter(c => c !== country) : [...list, country],
      };
    });
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await apiClient.put(`/student-auth/agreement-templates/${editing.id}`, form);
        toast.success('Template updated');
      } else {
        await apiClient.post('/student-auth/agreement-templates', form);
        toast.success('Template created — visible to all students');
      }
      setEditorOpen(false);
      loadTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (tpl) => {
    try {
      await apiClient.put(`/student-auth/agreement-templates/${tpl.id}`, { is_active: !tpl.is_active });
      toast.success(tpl.is_active ? 'Template deactivated' : 'Template activated');
      loadTemplates();
    } catch (err) {
      toast.error('Failed to update template');
    }
  };

  const handleDelete = async (tpl) => {
    const confirmMsg = tpl.signed_count
      ? `Delete "${tpl.title}"? Already-signed copies (${tpl.signed_count}) will be preserved for audit, but pending signatures will be removed.`
      : `Delete "${tpl.title}"? This cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await apiClient.delete(`/student-auth/agreement-templates/${tpl.id}`);
      toast.success('Template deleted');
      loadTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete template');
    }
  };

  return (
    <div className="space-y-4" data-testid="agreement-templates-manager">
      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <i className="fas fa-file-signature text-indigo-600"></i>
              Agreement Templates
            </CardTitle>
            <CardDescription>
              Create reusable consent forms & agreements. Active templates appear in <strong>every student&apos;s portal</strong> for e-signature.
            </CardDescription>
          </div>
          <Button
            onClick={openCreate}
            className="bg-[#192650] hover:bg-[#0f1a38]"
            data-testid="create-agreement-template-btn"
          >
            <i className="fas fa-plus mr-2"></i>New Template
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 text-center text-gray-400">
              <i className="fas fa-spinner fa-spin mr-2"></i>Loading templates...
            </div>
          ) : templates.length === 0 ? (
            <div className="py-16 text-center" data-testid="no-templates-empty">
              <i className="fas fa-file-contract text-4xl text-gray-300 mb-3"></i>
              <p className="text-sm font-semibold text-gray-700">No agreement templates yet</p>
              <p className="text-xs text-gray-500 mt-1">Create your first template — it will appear in every student&apos;s Agreements tab.</p>
              <Button
                onClick={openCreate}
                variant="outline"
                className="mt-4"
                size="sm"
              >
                <i className="fas fa-plus mr-2"></i>Create Your First Template
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {templates.map((tpl) => (
                <div
                  key={tpl.id}
                  className="border rounded-lg p-4 hover:border-indigo-300 transition-colors bg-white"
                  data-testid={`template-row-${tpl.id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-sm font-semibold text-gray-900">{tpl.title}</h4>
                        {tpl.is_active ? (
                          <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-1.5"></span>
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] bg-gray-100 text-gray-600 border-gray-200">
                            Inactive
                          </Badge>
                        )}
                        {(!tpl.countries || tpl.countries.length === 0) ? (
                          <Badge variant="outline" className="text-[10px] bg-sky-50 text-sky-700 border-sky-200">
                            <i className="fas fa-globe mr-1"></i>All countries
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200">
                            <i className="fas fa-flag mr-1"></i>
                            {tpl.countries.length === 1 ? tpl.countries[0] : `${tpl.countries.length} countries`}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                        {tpl.content?.substring(0, 160)}{tpl.content?.length > 160 ? '...' : ''}
                      </p>
                      <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-gray-500">
                        <span><i className="fas fa-check-circle text-emerald-500 mr-1"></i>{tpl.signed_count || 0} signed</span>
                        <span><i className="fas fa-clock text-amber-500 mr-1"></i>{tpl.pending_count || 0} pending</span>
                        {tpl.countries && tpl.countries.length > 1 && (
                          <span className="text-indigo-600">{tpl.countries.join(', ')}</span>
                        )}
                        {tpl.created_by_name && <span>By {tpl.created_by_name}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="flex items-center gap-2 mr-2">
                        <span className="text-xs text-gray-600">Active</span>
                        <Switch
                          checked={tpl.is_active !== false}
                          onCheckedChange={() => handleToggleActive(tpl)}
                          data-testid={`toggle-template-${tpl.id}`}
                        />
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEdit(tpl)}
                        data-testid={`edit-template-${tpl.id}`}
                      >
                        <i className="fas fa-edit mr-1"></i>Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(tpl)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-template-${tpl.id}`}
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Editor Dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Template' : 'Create Agreement Template'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'Changes to pending (unsigned) copies will update instantly. Signed copies remain unchanged.'
                : 'This template will be shown to every active student in their portal for e-signature.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="tpl-title">Title</Label>
              <Input
                id="tpl-title"
                data-testid="template-title-input"
                placeholder="e.g., Service Agreement, Data Consent, Code of Conduct"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tpl-content">Content</Label>
              <Textarea
                id="tpl-content"
                data-testid="template-content-input"
                rows={14}
                placeholder={`Type the full agreement text here.\n\nExample:\n1. I agree to pay all fees on time...\n2. I authorize Patrick's Edu Care to share my documents with universities...\n3. I consent to receive communications via email/WhatsApp...`}
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                className="font-mono text-sm"
              />
              <p className="text-[11px] text-gray-500">
                Tip: Use clear numbered clauses. Students will read this and sign with a canvas signature.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Country Scope</Label>
              <div className="rounded-lg border bg-slate-50 p-3 space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  {form.countries.length === 0 ? (
                    <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200 text-xs">
                      <i className="fas fa-globe mr-1"></i>Visible to all students (global)
                    </Badge>
                  ) : (
                    form.countries.map(c => (
                      <Badge
                        key={c}
                        variant="outline"
                        className="bg-indigo-50 text-indigo-700 border-indigo-200 text-xs cursor-pointer hover:bg-indigo-100"
                        onClick={() => toggleCountry(c)}
                        data-testid={`selected-country-${c}`}
                      >
                        {c} <i className="fas fa-times ml-1.5 text-[9px]"></i>
                      </Badge>
                    ))
                  )}
                </div>
                <Input
                  placeholder="Search countries to add..."
                  value={countryQuery}
                  onChange={(e) => setCountryQuery(e.target.value)}
                  className="h-9 text-sm bg-white"
                  data-testid="country-search-input"
                />
                {countryQuery && (
                  <div className="max-h-40 overflow-y-auto bg-white rounded border divide-y">
                    {allCountries
                      .filter(c =>
                        c.toLowerCase().includes(countryQuery.toLowerCase()) && !form.countries.includes(c)
                      )
                      .slice(0, 20)
                      .map(c => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => { toggleCountry(c); setCountryQuery(''); }}
                          className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 flex items-center justify-between"
                          data-testid={`add-country-${c}`}
                        >
                          <span>{c}</span>
                          <i className="fas fa-plus text-[10px] text-indigo-500"></i>
                        </button>
                      ))
                    }
                    {allCountries.filter(c =>
                      c.toLowerCase().includes(countryQuery.toLowerCase()) && !form.countries.includes(c)
                    ).length === 0 && (
                      <p className="text-xs text-gray-400 px-3 py-2">No matches</p>
                    )}
                  </div>
                )}
                <p className="text-[11px] text-gray-500 leading-snug">
                  Leave empty to show the agreement to <strong>all students</strong>. Select specific countries to scope this agreement (e.g. Georgia-only regulatory consent).
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3 bg-slate-50">
              <div>
                <Label className="text-sm">Active</Label>
                <p className="text-xs text-gray-600">
                  {form.is_active ? 'Visible to all students' : 'Hidden from students'}
                </p>
              </div>
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                data-testid="template-active-toggle"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#192650] hover:bg-[#0f1a38]"
              data-testid="save-template-btn"
            >
              {saving ? (
                <><i className="fas fa-spinner fa-spin mr-2"></i>Saving...</>
              ) : editing ? 'Save Changes' : 'Create & Publish'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
